import { useState } from "react";
import { UsersRound } from "lucide-react";
import { api } from "@/lib/api";
import { Section, ActionDialog } from "./kit";
import { Field, Notice, Stepper, V3Actions, Compare } from "./v3";

// C106: participant and companion seats are separate pools; confirmed seats are never reduced away.
export function SeatPanel({ d, reload }) {
  const a = d.activity;
  const s = d.summary;
  const [cap, setCap] = useState(s.capacity || Math.max(1, s.seats_taken));
  const [comp, setComp] = useState(s.companion_capacity || 0);
  const [open, setOpen] = useState(false);
  const editable = d.can.manage && !["abgesagt", "archiviert"].includes(a.status);
  return (
    <Section title="Plätze und Begleitplatzregel" testId="activity-seats">
      <div className="space-y-2.5">
        {editable ? (
          <>
            <Stepper id="seats-capacity" label="Teilnehmendenplätze" icon={UsersRound} value={cap} min={Math.max(1, s.seats_taken)} max={500} onChange={setCap} />
            <Stepper id="seats-companions" label="Begleitplätze" icon={UsersRound} value={comp} min={s.companions_confirmed} max={100} onChange={setComp} />
          </>
        ) : (
          <>
            <Field label="Teilnehmendenplätze" testId="seats-capacity-ro">{s.capacity ?? "nicht festgelegt"}</Field>
            <Field label="Begleitplätze" testId="seats-companions-ro">{s.companion_capacity}</Field>
          </>
        )}
        <Field label="Teilnahmebestätigungen" testId="seats-confirmed">{s.seats_taken}</Field>
        <Field label="Begleitbestätigungen" testId="seats-companions-confirmed">{s.companions_confirmed}{s.companions_open ? ` · ${s.companions_open} angefragt` : ""}</Field>
        <Field label="Warteliste" testId="seats-waitlist">{s.waitlist ? `${s.waitlist} Person(en) · Nachrücken nur nach Entscheidung` : "Niemand auf der Warteliste"}</Field>
        <Notice title="Getrennte Platztypen" text="Eine Teilnahmeanfrage enthält keine automatische Begleitplatzbestätigung." testId="seats-rule" />
        {editable && <V3Actions primary="Regel prüfen" onPrimary={() => setOpen(true)} secondary={null} testId="seats" primaryDisabled={cap === s.capacity && comp === s.companion_capacity} />}
      </div>
      <ActionDialog open={open} onOpenChange={setOpen} title="Begleitplatzregel speichern" testId="seats-dialog" submitLabel="Plätze speichern" onDone={reload}
        description={`Neu: ${cap} ${cap === 1 ? "Teilnehmendenplatz" : "Teilnehmendenplätze"} und ${comp} ${comp === 1 ? "Begleitplatz" : "Begleitplätze"}. Bereits bestätigte Plätze bleiben bestehen.`}
        fields={[{ name: "reason", label: "Grund", type: "textarea", required: true }]}
        onSubmit={async (v) => { await api.put(`/manage/activities/${a.id}/seats`, { capacity: cap, companion_capacity: comp, reason: v.reason }); return "Plätze gespeichert."; }} />
    </Section>
  );
}

// C105: a full activity never overbooks; the conflict is shown with the current numbers and the waitlist alternative.
export function CapacityConflict({ d, name, onReload, onClose }) {
  return (
    <div className="mb-4 space-y-2.5 rounded-2xl border border-[#F3C6D7] bg-white p-4" role="alert" data-testid="capacity-conflict">
      <p className="text-lg font-bold text-brand-purple">Warteliste und Kapazitätskonflikt</p>
      <p className="text-sm text-zinc-600">Aktualisiere die Kapazität, bevor du eine weitere Teilnahme bestätigst.</p>
      <Compare before={d.activity.title_de} after={`${d.summary.seats_taken} von ${d.summary.capacity} bestätigt`} />
      <Field label="Offene Anfrage" testId="capacity-conflict-name">{name}</Field>
      <Field label="Alternative">Warteliste prüfen · Keine Überbuchung</Field>
      <V3Actions primary="Kapazität neu laden" onPrimary={onReload} secondary="Zurück" onSecondary={onClose} testId="capacity-conflict" />
    </div>
  );
}
